import { useState } from 'react'
import { UnifiedIngestionDialog } from '@/components/ingestion/UnifiedIngestionDialog'
import { JourneyDropdown } from './JourneyDropdown'
import { Upload, Search, Bot, AlertCircle } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { cn } from '@/lib/utils'

// Dot colors keyed by companyType — mirrors portfolioConstants without the import dep
const HEADER_DOT: Record<string, string> = {
  concept: 'var(--color-market)',
  startup: 'var(--color-accent)',
  scale_up: 'var(--color-business)',
  grown_up: 'var(--color-solution)',
  enterprise: 'var(--color-fg)',
}

interface Project {
  id: string
  name: string
  slug: string
  role: string
  companyType?: string | null
}

interface HeaderProps {
  currentProject?: Project | null
  onSwitchProject?: () => void
  onToggleHermesChat?: () => void
  isHermesChatOpen?: boolean
}

export function Header({
  currentProject,
  onSwitchProject,
  onToggleHermesChat,
  isHermesChatOpen,
}: HeaderProps) {
  const [showImport, setShowImport] = useState(false)
  const { user } = useAuthStore()
  const { journey } = useJourneyStore()

  // Count contested assumptions (placeholder — could be derived from journey/validation data)
  const contestedCount = 0

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  return (
    <>
      <header className="h-14 bg-white border-b border-warm-200 flex items-center justify-between px-4 flex-shrink-0">
        {/* Left: Brand + Project */}
        <div className="flex items-center gap-3 min-w-0">
          <a href="/" className="flex items-center gap-2 group" aria-label="Skerpa home">
            <img
              src="/brand/skerpa-mark.svg"
              alt=""
              aria-hidden="true"
              className="h-6 w-6 shrink-0"
            />
            <span className="text-base font-display font-bold text-text tracking-tight">
              Skerpa
            </span>
          </a>
          {currentProject && (
            <>
              <span className="text-warm-300">|</span>
              <button
                onClick={onSwitchProject}
                className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text transition-colors truncate max-w-[180px]"
              >
                <span
                  className="shrink-0 w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor: currentProject.companyType
                      ? HEADER_DOT[currentProject.companyType] ?? 'var(--color-fg-muted)'
                      : 'var(--color-fg-muted)',
                  }}
                />
                {currentProject.name}
              </button>
            </>
          )}
        </div>

        {/* Center: Journey Dropdown */}
        <div className="flex items-center">
          <JourneyDropdown />
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          {/* Contested assumptions badge */}
          {contestedCount > 0 && (
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-warm-100 rounded-lg transition-colors">
              <AlertCircle className="w-3.5 h-3.5 text-solution" />
              {contestedCount} contested
            </button>
          )}

          {/* Upload */}
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-text-secondary hover:text-text hover:bg-warm-100 rounded-lg transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload</span>
          </button>

          {/* Search */}
          <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-text-secondary hover:text-text hover:bg-warm-100 rounded-lg transition-colors">
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">Search</span>
          </button>

          {/* Skerpa toggle */}
          <button
            onClick={onToggleHermesChat}
            title="Skerpa Coach"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
              isHermesChatOpen
                ? 'text-business bg-business/10'
                : 'text-text-muted hover:text-text hover:bg-warm-100'
            )}
          >
            <Bot className="w-[18px] h-[18px]" />
          </button>

          {/* Profile avatar */}
          <button
            onClick={onSwitchProject}
            className="w-8 h-8 rounded-full bg-warm-200 flex items-center justify-center text-xs font-medium text-text-secondary hover:bg-warm-300 transition-colors"
            title={user?.name || 'Profile'}
          >
            {initials}
          </button>
        </div>
      </header>

      <UnifiedIngestionDialog open={showImport} onOpenChange={setShowImport} />
    </>
  )
}
